import { FunctionComponent, useEffect, useState } from 'react'
import { css } from '@emotion/css'
import { Theme, useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import TileSelect, { TileOption } from '@/components/TileSelect'
import CustomTipInput from '@/components/TipSelect/CustomTipInput'
import CustomTipModal from '@/components/TipSelect/CustomTipModal'
import { calcTipAmount } from '@/libs/helpers/utils'

const tipTileStyles = (theme: Theme, columns: number): string => css`
  display: grid;
  grid-template-columns: repeat(${columns}, 1fr);
  gap: 8px;

  @media (max-width: ${theme.breakpoints.SM}) {
    grid-template-columns: 1fr;
  }
`

interface TipSelectProps {
  /** Optional onChange callback to hook into tip amount change event */
  onChange?: (tipAmount: string) => void
  totalAfterTax: string
  isCartLoading: boolean
  hasNoTipFF?: boolean
}

const NO_TIP = 'no_tip'
const CUSTOM = 'custom'
const DEFAULT_TIP = '20'

const TipSelect: FunctionComponent<TipSelectProps> = ({
  onChange,
  totalAfterTax,
  isCartLoading,
  hasNoTipFF = false,
}) => {
  // STATES
  const [openCustomTip, setOpenCustomTip] = useState<boolean>(false)
  const [customTipAmount, setCustomTipAmount] = useState<string>('')
  const [selectedTip, setSelectedTip] = useState<string>(DEFAULT_TIP)
  const [previousPreset, setPreviousPreset] = useState<string>(DEFAULT_TIP)
  const theme = useTheme()

  // HOOKS
  const { t } = useTranslation()

  // CONSTS
  const denominations = ['10', '15', '20', '25', CUSTOM]
  const tipDenominations = hasNoTipFF ? [NO_TIP, ...denominations] : denominations
  const isCustomTip = selectedTip === CUSTOM
  const isNoTip = selectedTip === NO_TIP
  const isPreviousNoTip = previousPreset === NO_TIP
  const seedDollar = isPreviousNoTip ? '0.00' : calcTipAmount(totalAfterTax, previousPreset)
  const seedPercent = isPreviousNoTip ? '' : previousPreset

  // EFFECTS
  useEffect(() => {
    if (!onChange) {
      return
    }
    if (isNoTip) {
      onChange('0.00')
      return
    }
    if (!isCustomTip) {
      const newTipAmount = calcTipAmount(totalAfterTax, selectedTip)
      if (newTipAmount) {
        onChange(newTipAmount)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAfterTax, selectedTip, isCustomTip, isNoTip])

  // HANDLERS
  const handleCustomTipChange = (newTipAmount: string): void => {
    setCustomTipAmount(newTipAmount)
    if (onChange) {
      onChange(newTipAmount)
    }
  }

  const tipOptions = tipDenominations.map((tipDenomination: string) => {
    const renderTipAmount = (): TileOption['subtitle'] => {
      if (tipDenomination === NO_TIP) {
        return { value: null }
      }
      const isCurrentTipCustom = tipDenomination === CUSTOM
      if (isCurrentTipCustom && (!isCustomTip || hasNoTipFF)) {
        return { value: null }
      }

      const tipEstimateValue = isCurrentTipCustom
        ? customTipAmount
        : calcTipAmount(totalAfterTax, tipDenomination)

      return {
        value: t('checkout.payment.tip_amount', {
          value: tipEstimateValue,
        }),
        align: 'center',
        m: '4px 0 0 0',
      }
    }

    const option: TileOption = {
      value: tipDenomination,
      label: t('checkout.payment.tip_select', { context: tipDenomination }),
      subtitle: renderTipAmount(),
    }

    if (tipDenomination === CUSTOM && !hasNoTipFF) {
      option.onClick = (): void => {
        setOpenCustomTip(true)
      }
    }

    return option
  })

  const handleTipTileChange = (tipDenomination: string): void => {
    if (tipDenomination !== CUSTOM) {
      setPreviousPreset(tipDenomination)
    }
    setSelectedTip(tipDenomination)
  }

  return (
    <>
      <TileSelect
        value={selectedTip}
        options={tipOptions}
        name="tip-select"
        onChange={handleTipTileChange}
        className={tipTileStyles(theme, tipDenominations.length)}
      />
      {hasNoTipFF && isCustomTip && (
        <CustomTipInput
          seedDollar={seedDollar}
          seedPercent={seedPercent}
          totalAfterTax={totalAfterTax}
          onChange={handleCustomTipChange}
        />
      )}
      {!hasNoTipFF && (
        <CustomTipModal
          onClose={(): void => setOpenCustomTip(false)}
          onChange={handleCustomTipChange}
          selectedTip={selectedTip}
          open={openCustomTip}
          totalAfterTax={totalAfterTax}
          isCartLoading={isCartLoading}
        />
      )}
    </>
  )
}

export default TipSelect
